import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MeetingService } from '../meeting.service';
import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import { MEETING_STATUS, Participant } from '@/types/meeting.types';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { getInspectConvenedMeetingFormObject } from './utils/convened-meeting-form-object';
import { DynamicForm } from '@/components/form-builder/form-builder.types';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { StatusComponent } from '@/components/shared/status/status.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { MeetingWorkflowBlocksService } from '../meeting-workflow-blocks.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { BehaviorSubject } from 'rxjs';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { MeetingTransformationService } from '../meeting-transformation.service';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { getExtraitsPvMeetingDrawer } from '../meeting-convene/utils/extraits-pv-meeting-drawer-object';
import { DataTableAction, DataTableRowActions } from '@/components/data-table-builder/data-table.types';

@Component({
  selector: 'sm-inspect-convened-meeting',
  imports: [PageHeaderComponent, FormBuilderComponent, StatusComponent, BlockBuilderComponent, DrawerComponent],
  templateUrl: './inspect-convened-meeting.component.html',
  styleUrl: './inspect-convened-meeting.component.scss',
})
export class InspectConvenedMeetingComponent implements OnInit {
  activatedRoute = inject(ActivatedRoute);
  meetingService = inject(MeetingService);
  meetingWorkflowBlocksService = inject(MeetingWorkflowBlocksService);
  meetingTransformationService = inject(MeetingTransformationService);
  meetingRepository = inject(MeetingRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);

  id = this.activatedRoute.snapshot.params['id'];

  status: MEETING_STATUS = MEETING_STATUS.VALIDER;

  @ViewChild('dialog') dialogComponent!: DialogComponent;

  @ViewChild('drawerPvExtraits') drawerPvExtraits!: DrawerComponent;
  pvExtraitsDrawerObject?: DrawerObject;

  breadcrumb = [
    {
      label: 'meeting.breadcrumb.meeting',
      routerLink: ['/meetings'],
    },
    {
      label: 'meeting.breadcrumb.inspect',
      routerLink: ['/meetings', 'inspect'],
    },
  ];

  isPVNull$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  haveSnippets = false;
  acId: number | null = null;

  invitedMembers: Participant[] = [];
  plannedDossiers: any[] = [];

  formObject: DynamicForm = getInspectConvenedMeetingFormObject({
    store: this.meetingRepository,
    status: this.status,
    plannedDossiers: this.plannedDossiers,
  });

  controlBlocks: DynamicBlocks = this.meetingWorkflowBlocksService.getInspectConvenedBlocks({
    inspectPV: () => this.meetingTransformationService.inspectPv(),
    checkPvExtraits: this.haveSnippets ? () => this.drawerPvExtraits.toggleVisible() : undefined,
  });

  rowActionsFn: (target: any) => DataTableRowActions = (target: any) => {
    this.acId = target.ac.id;
    const additionalActions: Record<string, DataTableAction[] | undefined> = {};

    additionalActions['snippetsActions'] = [
      {
        label: 'meeting.snippets.table.actions.download',
        icon: 'pi pi-download',
        action: () => this.meetingTransformationService.inspectSnippets(target.doc),
      },
    ];

    return {
      additionalActions,
    };
  };

  ngOnInit() {
    this.loadData();

    this.pvExtraitsDrawerObject = getExtraitsPvMeetingDrawer({
      comType: '',
      store: this.meetingRepository,
      data: [],
      totalRecords: 0,
      rowActionsFn: this.rowActionsFn,
    });
  }

  loadData() {
    this.meetingService.getOne(this.id).subscribe({
      next: response => {
        this.status = response.status;
        this.haveSnippets = response.haveExtraits ?? false;
        this.isPVNull$.next(!response.processVerbal);
        this.meetingTransformationService.setResponseToDtoOnLoadData(response);
        this.meetingRepository.set('serverState', this.meetingRepository.get('dto'));
        this.meetingRepository.set('comSelected', response.com ? { name: response.com.nom, code: response.com.id } : undefined);
        this.invitedMembers = response.participants;
        this.plannedDossiers = this.meetingTransformationService.mergePlannedDossiers();
        this.formObject = getInspectConvenedMeetingFormObject({
          store: this.meetingRepository,
          invitedMembers: this.invitedMembers,
          status: this.status,
          plannedDossiers: this.plannedDossiers,
        });

        this.controlBlocks = this.meetingWorkflowBlocksService.getInspectConvenedBlocks({
          inspectPV: () => this.meetingTransformationService.inspectPv(),
          checkPvExtraits: this.haveSnippets ? () => this.drawerPvExtraits.toggleVisible() : undefined,
        });

        if (this.haveSnippets) {
          this.meetingService.getSnippetsByMeetingId(this.id).subscribe({
            next: snippet => {
              this.pvExtraitsDrawerObject = getExtraitsPvMeetingDrawer({
                comType: response.com.comType.code,
                store: this.meetingRepository,
                data: snippet.content,
                totalRecords: snippet.totalElements,
                rowActionsFn: this.rowActionsFn,
              });
            },
            error: error => {
              console.log(error);
            },
          });
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('meeting.message.error.loading'),
        });
      },
    });
  }
}
